// Gom toàn bộ dữ liệu 360 của một khách CMS: hồ sơ POS khớp SĐT/liên kết tay → hóa đơn, thẻ, ảnh, chữ ký, chỉ số
import { useMemo } from 'react';
import type { CustomerDetailData } from '../../api/ChannelCustomer';
import usePosStore from '../POS/store';
import useTreatmentStore from '../Treatment/store';
import useC360Store from './store';
import {
  buildLedger, classify, collectCustomerData, customerMetrics, interestsOf, journeyOf, matchPosCustomers, nextActions, normPhone, signNeeds, visitsOf,
} from './logic';
import type { SignParty } from './templates';

export const customerKeyOf = (id: number | string) => `cms-${id}`;

export function phonesOf(c: CustomerDetailData | null) {
  if (!c) return [];
  return [c.phone, ...(c.phones || []).map((p: any) => p?.phone)].filter(Boolean) as string[];
}

export const genderLabel = (g?: number | null) => ({ 1: 'Nam', 2: 'Nữ', 0: 'Khác' } as Record<number, string>)[Number(g ?? -1)];

export default function useCustomer360(customer: CustomerDetailData | null) {
  const key = customer ? customerKeyOf(customer.id) : '';
  const posCustomers = usePosStore(s => s.customers);
  const orders = usePosStore(s => s.orders);
  const transactions = usePosStore(s => s.transactions);
  const refunds = usePosStore(s => s.refunds);
  const cards = useTreatmentStore(s => s.cards);
  const tcfg = useTreatmentStore(s => s.config);
  const docs = useC360Store(s => s.docs);
  const photos = useC360Store(s => s.photos);
  const cfg = useC360Store(s => s.config);
  const linked = useC360Store(s => (key ? s.links[key] : undefined));
  const override = useC360Store(s => (key ? s.overrides[key] : undefined));
  const visitRecords = useC360Store(s => s.visitRecords);

  return useMemo(() => {
    const phones = phonesOf(customer);
    const pos = customer ? matchPosCustomers(phones, posCustomers, linked || []) : [];
    const posIds = pos.map(p => p.id);
    const phoneSet = new Set(phones.map(normPhone));
    /** Hồ sơ gắn bằng liên kết tay (không trùng SĐT nào của khách) */
    const manualIds = pos.filter(p => !phoneSet.has(normPhone(p.phone))).map(p => p.id);
    const data = collectCustomerData(posIds, key, { orders, transactions, refunds, cards, docs, photos });
    const now = new Date();
    const metrics = customerMetrics(data, cfg, tcfg, now);
    const cls = classify(metrics, cfg, override, now);
    const visits = visitsOf(data, visitRecords.filter(r => posIds.includes(r.posCustomerId)), cfg, now);
    const needs = signNeeds(data, visits);
    const party: SignParty = {
      name: customer?.fullname || customer?.display_name || pos[0]?.name || '',
      phone: customer?.phone || pos[0]?.phone || '',
      birthday: customer?.birthday,
      gender: genderLabel(customer?.gender),
    };
    return {
      key, pos, posIds, manualIds, data, metrics, cls, party, cfg, tcfg,
      ledger: buildLedger(data),
      visits,
      needs,
      actions: nextActions(data, metrics, cls, tcfg, needs, now),
      journey: journeyOf(data, customer?.created_at),
      interests: interestsOf(data),
    };
  }, [customer, key, posCustomers, linked, orders, transactions, refunds, cards, docs, photos, cfg, tcfg, override, visitRecords]);
}

export type C360 = ReturnType<typeof useCustomer360>;
